/**
 * SEO Utility for Dynamic Page Optimization
 * Handles dynamic page titles, meta descriptions, and SEO enhancements
 * based on collection context and active filters
 */

class SEOUtility {
  constructor() {
    this.baseTitle = document.title;
    this.baseMeta = this.getMetaDescription();
    this.collectionData = null;
    this.activeFilters = new Map();
    
    this.init();
  }

  init() {
    this.loadCollectionData();
    this.bindFilterEvents();
    this.updateSEOElements();
  }

  loadCollectionData() {
    // Load collection taxonomy data
    const taxonomyScript = document.querySelector('.collection-taxonomy-data');
    if (taxonomyScript) {
      try {
        this.collectionData = JSON.parse(taxonomyScript.textContent);
      } catch (error) {
        console.warn('Error loading collection data for SEO:', error);
      }
    }

    // Load collection info from page
    this.collectionInfo = {
      title: this.getCollectionTitle(),
      handle: this.getCollectionHandle(),
      type: this.getCollectionType()
    };
  }

  bindFilterEvents() {
    // Listen for filter changes
    document.addEventListener('filtersChanged', (event) => {
      this.activeFilters = new Map(event.detail.filters);
      this.updateSEOElements();
    });

    // Listen for URL changes (back/forward)
    window.addEventListener('popstate', () => {
      this.parseActiveFilters();
      this.updateSEOElements();
    });

    // Parse initial filters from URL
    this.parseActiveFilters();
  }

  parseActiveFilters() {
    this.activeFilters.clear();
    const urlParams = new URLSearchParams(window.location.search);
    
    urlParams.forEach((value, key) => {
      if (key.startsWith('filter_')) {
        const filterType = key.replace('filter_', '');
        const values = value.split(',').filter(v => v.trim() !== '');
        if (values.length > 0) {
          this.activeFilters.set(filterType, values);
        }
      }
    });
  }

  updateSEOElements() {
    this.updatePageTitle();
    this.updateMetaDescription();
    this.updateCanonicalURL();
    this.updatePaginationLinks();
    this.updateBreadcrumbSchema();
  }

  updatePageTitle() {
    const dynamicTitle = this.generateDynamicTitle();
    document.title = dynamicTitle;
    
    // Update Open Graph title
    this.updateMetaProperty('og:title', dynamicTitle);
  }

  generateDynamicTitle() {
    const parts = [];
    
    // Add filter-specific terms
    if (this.activeFilters.size > 0) {
      const filterTerms = this.getFilterTermsForTitle();
      if (filterTerms.length > 0) {
        parts.push(...filterTerms);
      }
    }

    // Add collection name
    if (this.collectionInfo.title) {
      parts.push(this.collectionInfo.title);
    }

    // Add store name
    parts.push('Skid Steer Cabs');

    return parts.join(' | ');
  }

  getFilterTermsForTitle() {
    const terms = [];
    
    // Prioritize machine type and work type for titles
    const priorityOrder = ['machine_type', 'work_type', 'brand'];
    
    priorityOrder.forEach(filterType => {
      if (this.activeFilters.has(filterType)) {
        const values = this.activeFilters.get(filterType);
        const formattedValues = values.map(v => this.formatFilterValue(v));
        
        if (filterType === 'machine_type') {
          terms.push(...formattedValues);
        } else if (filterType === 'work_type') {
          terms.push(...formattedValues);
        } else if (filterType === 'brand' && values.length === 1) {
          terms.push(formattedValues[0]);
        }
      }
    });

    return terms.slice(0, 3); // Limit to 3 terms for title length
  }

  updateMetaDescription() {
    const dynamicDescription = this.generateDynamicDescription();
    this.updateMetaContent('description', dynamicDescription);
    this.updateMetaProperty('og:description', dynamicDescription);
  }

  generateDynamicDescription() {
    let description = '';
    
    if (this.activeFilters.size > 0) {
      description = this.generateFilteredDescription();
    } else {
      description = this.generateCollectionDescription();
    }

    // Add call-to-action and contact info
    description += ' Professional quality equipment with fast shipping. Call (240) 301-1225 for expert advice.';
    
    return description;
  }

  generateFilteredDescription() {
    const filterParts = [];
    
    // Build description from active filters
    if (this.activeFilters.has('machine_type')) {
      const machines = this.activeFilters.get('machine_type').map(v => this.formatFilterValue(v));
      filterParts.push(`${machines.join(' and ')} attachments`);
    }

    if (this.activeFilters.has('work_type')) {
      const workTypes = this.activeFilters.get('work_type').map(v => this.formatFilterValue(v));
      filterParts.push(`for ${workTypes.join(' and ')} applications`);
    }

    if (this.activeFilters.has('brand')) {
      const brands = this.activeFilters.get('brand').map(v => this.formatFilterValue(v));
      filterParts.push(`from ${brands.join(' and ')}`);
    }

    let description = `Shop ${filterParts.join(' ')}. `;
    
    // Add collection context
    if (this.collectionInfo.title) {
      description += `Browse our complete selection of ${this.collectionInfo.title.toLowerCase()}.`;
    }

    return description;
  }

  generateCollectionDescription() {
    const collectionName = this.collectionInfo.title || 'products';
    
    // Generate description based on collection type
    if (this.collectionInfo.handle.includes('attachments')) {
      return `Shop professional ${collectionName.toLowerCase()} for construction and landscaping. High-quality attachments compatible with major equipment brands.`;
    } else if (this.collectionInfo.handle.includes('doors') || this.collectionInfo.handle.includes('windows')) {
      return `Premium ${collectionName.toLowerCase()} for heavy equipment. Durable replacement parts designed for performance and longevity.`;
    } else {
      return `Browse our complete selection of ${collectionName.toLowerCase()}. Professional equipment and accessories for construction professionals.`;
    }
  }

  updateCanonicalURL() {
    const canonicalURL = this.generateCanonicalURL();
    this.updateLinkRel('canonical', canonicalURL);
  }

  generateCanonicalURL() {
    const baseURL = window.location.origin + window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    // For filtered views, use the base collection URL as canonical
    // This prevents duplicate content issues from filter combinations
    if (this.activeFilters.size > 0) {
      return baseURL;
    }

    // For pagination, include page parameter in canonical URL
    const page = urlParams.get('page');
    if (page && page !== '1') {
      return `${baseURL}?page=${page}`;
    }

    // For sort parameters, include in canonical if it's not the default
    const sortBy = urlParams.get('sort_by');
    if (sortBy && sortBy !== 'manual') {
      return `${baseURL}?sort_by=${sortBy}`;
    }

    // Default to base URL for clean canonical
    return baseURL;
  }

  updatePaginationLinks() {
    // Add prev/next link tags for pagination
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page') || '1');
    const baseURL = window.location.origin + window.location.pathname;

    // Remove existing pagination links
    const existingPrev = document.querySelector('link[rel="prev"]');
    const existingNext = document.querySelector('link[rel="next"]');
    if (existingPrev) existingPrev.remove();
    if (existingNext) existingNext.remove();

    // Add previous page link
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      const prevURL = prevPage === 1 ? baseURL : `${baseURL}?page=${prevPage}`;
      this.updateLinkRel('prev', prevURL);
    }

    // Add next page link (we'd need to know total pages for this)
    // For now, we'll add it optimistically and let the server handle 404s
    const nextURL = `${baseURL}?page=${currentPage + 1}`;
    this.updateLinkRel('next', nextURL);
  }

  updateBreadcrumbSchema() {
    const breadcrumbSchema = this.generateBreadcrumbSchema();
    this.updateStructuredData('breadcrumb', breadcrumbSchema);
  }

  generateBreadcrumbSchema() {
    const breadcrumbs = [
      { name: 'Home', url: window.location.origin },
      { name: 'Collections', url: window.location.origin + '/collections' }
    ];

    // Add collection breadcrumb
    if (this.collectionInfo.title) {
      breadcrumbs.push({
        name: this.collectionInfo.title,
        url: window.location.origin + window.location.pathname
      });
    }

    // Add filter breadcrumbs
    if (this.activeFilters.size > 0) {
      const filterTerms = this.getFilterTermsForTitle();
      if (filterTerms.length > 0) {
        breadcrumbs.push({
          name: filterTerms.join(' '),
          url: window.location.href
        });
      }
    }

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  }

  // Utility methods
  formatFilterValue(value) {
    return value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getCollectionTitle() {
    const titleElement = document.querySelector('.collection-title, h1');
    return titleElement ? titleElement.textContent.trim() : '';
  }

  getCollectionHandle() {
    const pathParts = window.location.pathname.split('/');
    const collectionsIndex = pathParts.indexOf('collections');
    return collectionsIndex !== -1 && pathParts[collectionsIndex + 1] 
      ? pathParts[collectionsIndex + 1] 
      : '';
  }

  getCollectionType() {
    const handle = this.getCollectionHandle();
    if (handle.includes('attachments')) return 'attachments';
    if (handle.includes('doors') || handle.includes('windows')) return 'replacement-parts';
    return 'general';
  }

  getMetaDescription() {
    const metaDesc = document.querySelector('meta[name="description"]');
    return metaDesc ? metaDesc.getAttribute('content') : '';
  }

  updateMetaContent(name, content) {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  }

  updateMetaProperty(property, content) {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  }

  updateLinkRel(rel, href) {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  updateStructuredData(type, data) {
    // Remove existing structured data of this type
    const existing = document.querySelector(`script[type="application/ld+json"][data-schema="${type}"]`);
    if (existing) {
      existing.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', type);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }
}

// Initialize SEO utility on collection pages
document.addEventListener('DOMContentLoaded', () => {
  if (document.body.classList.contains('template-collection') || 
      window.location.pathname.includes('/collections/')) {
    window.seoUtility = new SEOUtility();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SEOUtility;
}
